import React, { useState, useRef } from 'react';
import { Button, Table, Modal, Form } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Get, Post, PostFormData, Put } from '../../../../utils/httpProvider';
import { ACCOUNT_API, ACCOUNT_AUTHINFO_API, ROLE_API, HOPDONGCHITIET_API, LICHHOCTHUCHANH_API } from '../../../../utils/apiUrl';

const StudentTab = ({
    contractId,
    studentsData,
    isStudentsLoading,
    refetchStudents
}) => {
    const studentFileInputRef = useRef(null);

    // Modal states for manual Student creation
    const [showAddStudentModal, setShowAddStudentModal] = useState(false);
    const [newStudent, setNewStudent] = useState({ fullName: '', phoneNumber: '', identityCard: '', email: '', address: '', dateOfBirth: '', pseudonym: '' });

    // States for Batch Account Creation after Excel Import
    const [showConfirmCreateAccountsModal, setShowConfirmCreateAccountsModal] = useState(false);
    const [studentsToCreateAccounts, setStudentsToCreateAccounts] = useState([]);
    const [allImportedStudents, setAllImportedStudents] = useState([]);
    const [isCreatingAccounts, setIsCreatingAccounts] = useState(false);

    // --- HELPERS ---
    const splitFullName = (fullName) => {
        if (!fullName) return { lastName: '', middleName: '', firstName: '' };
        const parts = fullName.trim().split(/\s+/);
        if (parts.length === 1) return { lastName: '', middleName: '', firstName: parts[0] };
        if (parts.length === 2) return { lastName: parts[0], middleName: '', firstName: parts[1] };
        const lastName = parts[0];
        const firstName = parts[parts.length - 1];
        const middleName = parts.slice(1, parts.length - 1).join(' ');
        return { lastName, middleName, firstName };
    };

    const checkAccountExistsOnSso = async (username) => {
        if (!username) return null;
        try {
            const res = await Get(`${ACCOUNT_API.GET_PAGING_ACCOUNT}?Keyword=${username}`);
            const items = res?.data?.$values || res?.data || [];
            const found = items.find(acc => {
                const accUsername = acc.username || acc.Username || acc.userName || acc.UserName;
                return accUsername && accUsername.toLowerCase() === username.toLowerCase();
            });
            return found ? (found.id || found.Id) : null;
        } catch (err) {
            return null;
        }
    };

    // --- MANUAL STUDENT ACTIONS ---
    const handleOpenAddStudent = () => {
        setNewStudent({ fullName: '', phoneNumber: '', identityCard: '', email: '', address: '', dateOfBirth: '', pseudonym: '' });
        setShowAddStudentModal(true);
    };

    const handleSaveStudent = async () => {
        if (!newStudent.fullName || !newStudent.identityCard) {
            toast.warning("Họ tên và Số CCCD là bắt buộc.");
            return;
        }
        try {
            let resolvedId = await checkAccountExistsOnSso(newStudent.identityCard);

            if (!resolvedId) {
                let targetRoleId = null;
                try {
                    const roleRes = await Get(`${ROLE_API.GET_ITEM_ROLE}/HOC_VIEN_NGOAI_VIEN`);
                    if (roleRes) {
                        targetRoleId = roleRes.id || roleRes.Id;
                    }
                } catch (roleErr) {
                    console.error("Failed to fetch role from SSO via GetItemRole", roleErr);
                }

                if (!targetRoleId) {
                    throw new Error("Không tìm thấy thông tin quyền 'HOC_VIEN_NGOAI_VIEN' trên hệ thống SSO.");
                }

                const { lastName, middleName, firstName } = splitFullName(newStudent.fullName);
                const username = newStudent.identityCard;
                const payload = [{
                    username: username,
                    lastName: lastName || '-',
                    middleName: middleName || '',
                    firstName: firstName || '-',
                    email: newStudent.email || `${username}@hldt.vn`,
                    phoneNumber: newStudent.phoneNumber || "",
                    pseudonym: newStudent.pseudonym || "",
                    password: "123456",
                    confirmPassword: "123456",
                    isActive: true,
                    currentUserName: "system",
                    roles: [
                        {
                            id: targetRoleId,
                            roleCode: "HOC_VIEN_NGOAI_VIEN",
                            name: "Học viên ngoại viện"
                        }
                    ]
                }];

                await Post(ACCOUNT_API.ADD_MANY_ACCOUNT, null, payload);
                resolvedId = await checkAccountExistsOnSso(username);
                if (!resolvedId) {
                    throw new Error("Tạo tài khoản SSO thành công nhưng không lấy được ID.");
                }
            }

            await Post(`${HOPDONGCHITIET_API.ADD_HOCVIEN_MANUALLY}/${contractId}`, null, resolvedId);
            toast.success("Thêm học viên và liên kết tài khoản thành công!");
            setShowAddStudentModal(false);
            refetchStudents();
        } catch (err) {
            toast.error(err?.message || "Không thể thêm học viên.");
        }
    };

    // --- EXCEL IMPORT STUDENT ACTIONS ---
    const handleUploadStudent = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const listImported = await PostFormData(`${HOPDONGCHITIET_API.IMPORT_HOCVIEN}/${contractId}`, formData);
            const rawList = listImported?.$values || listImported || [];

            const checkedList = await Promise.all(
                rawList.map(async (student) => {
                    const cccd = student.identityCard;
                    const resolvedId = await checkAccountExistsOnSso(cccd);

                    if (resolvedId && student.pseudonym) {
                        try {
                            const accInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, resolvedId);
                            const profile = accInfo?.accountAuthInfo || accInfo?.AccountAuthInfo || accInfo;
                            const currentPseudonym = profile?.pseudonym || profile?.Pseudonym;
                            if (!currentPseudonym || currentPseudonym !== student.pseudonym) {
                                const updatePayload = {
                                    id: profile.id || profile.Id,
                                    accountId: resolvedId,
                                    firstName: profile.firstName || profile.FirstName || "",
                                    lastName: profile.lastName || profile.LastName || "",
                                    middleName: profile.middleName || profile.MiddleName || "",
                                    email: profile.email || profile.Email || "",
                                    phoneNumber: profile.phoneNumber || profile.PhoneNumber || "",
                                    pseudonym: student.pseudonym,
                                };
                                const updateUrl = `${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT.replace('/GetItem', '/UpdateItem')}/${profile.id || profile.Id}`;
                                await Put(updateUrl, updatePayload);
                            }
                        } catch (updateErr) {
                            console.error("Failed to update pseudonym for existing student on SSO during import", updateErr);
                        }
                    }

                    return { ...student, accountId: resolvedId };
                })
            );

            setAllImportedStudents(checkedList);

            const unlinked = checkedList.filter(student =>
                !student.accountId ||
                student.accountId === '00000000-0000-0000-0000-000000000000'
            );

            if (unlinked.length > 0) {
                setStudentsToCreateAccounts(unlinked);
                setShowConfirmCreateAccountsModal(true);
            } else {
                const finalAccountIds = checkedList.map(s => s.accountId).filter(Boolean);
                await Post(`${HOPDONGCHITIET_API.SAVE_AND_LINK_STUDENTS}/${contractId}`, null, finalAccountIds);
                toast.success("Đã nhập danh sách học viên từ file Excel và liên kết thành công!");
                refetchStudents();
            }
        } catch (err) {
            toast.error(err?.message || 'Có lỗi xảy ra trong quá trình import file.');
        } finally {
            if (studentFileInputRef.current) studentFileInputRef.current.value = '';
        }
    };

    const handleCreateAccounts = async () => {
        setIsCreatingAccounts(true);
        try {
            let targetRoleId = null;
            try {
                const roleRes = await Get(`${ROLE_API.GET_ITEM_ROLE}/HOC_VIEN_NGOAI_VIEN`);
                if (roleRes) {
                    targetRoleId = roleRes.id || roleRes.Id;
                }
            } catch (roleErr) {
                console.error("Failed to fetch role from SSO via GetItemRole", roleErr);
            }

            if (!targetRoleId) {
                throw new Error("Không tìm thấy thông tin quyền 'HOC_VIEN_NGOAI_VIEN' trên hệ thống SSO.");
            }

            const payload = studentsToCreateAccounts.map(student => {
                const { lastName, middleName, firstName } = splitFullName(student.fullName);
                const username = student.identityCard;
                return {
                    username: username,
                    lastName: lastName || '-',
                    middleName: middleName || '',
                    firstName: firstName || '-',
                    email: student.email || `${username}@hldt.vn`,
                    phoneNumber: student.phoneNumber || "",
                    pseudonym: student.pseudonym || "",
                    password: "123456",
                    confirmPassword: "123456",
                    isActive: true,
                    currentUserName: "system",
                    roles: [
                        {
                            id: targetRoleId,
                            roleCode: "HOC_VIEN_NGOAI_VIEN",
                            name: "Học viên ngoại viện"
                        }
                    ]
                };
            });

            await Post(ACCOUNT_API.ADD_MANY_ACCOUNT, null, payload);
            toast.success(`Đã tạo thành công ${payload.length} tài khoản lên SSO!`);

            const finalStudents = await Promise.all(
                allImportedStudents.map(async (student) => {
                    if (student.accountId && student.accountId !== '00000000-0000-0000-0000-000000000000') {
                        return student;
                    }
                    const username = student.identityCard;
                    const resolvedId = await checkAccountExistsOnSso(username);
                    return { ...student, accountId: resolvedId };
                })
            );

            const finalAccountIds = finalStudents.map(s => s.accountId).filter(Boolean);
            await Post(`${HOPDONGCHITIET_API.SAVE_AND_LINK_STUDENTS}/${contractId}`, null, finalAccountIds);
            toast.success("Đồng bộ kết nối tài khoản và liên kết lớp học thành công!");
            refetchStudents();
        } catch (err) {
            toast.error(err?.message || 'Có lỗi xảy ra trong quá trình tạo tài khoản.');
        } finally {
            setIsCreatingAccounts(false);
            setShowConfirmCreateAccountsModal(false);
            setStudentsToCreateAccounts([]);
            setAllImportedStudents([]);
        }
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4" style={{ gap: '12px' }}>
                <div className="mb-2 mb-sm-0">
                    <h5 className="font-weight-bold text-dark mb-1">Học viên tham gia khóa thực hành</h5>
                    <div className="text-muted small">Danh sách chi tiết các học viên đăng ký thực hành lâm sàng</div>
                </div>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        style={{ borderRadius: '8px', padding: '8px 16px', whiteSpace: 'nowrap' }}
                        onClick={handleOpenAddStudent} disabled={!contractId}
                    >
                        <i className="fas fa-plus mr-2"></i> Thêm học viên
                    </Button>
                    <input
                        type="file"
                        ref={studentFileInputRef}
                        style={{ display: 'none' }}
                        accept=".xlsx, .xls"
                        onChange={handleUploadStudent}
                    />
                    <Button
                        variant="success"
                        size="sm"
                        style={{ borderRadius: '8px', padding: '8px 16px', whiteSpace: 'nowrap' }}
                        onClick={() => studentFileInputRef.current?.click()} disabled={!contractId}
                    >
                        <i className="fas fa-file-excel mr-2"></i> Import danh sách học viên
                    </Button>
                </div>
            </div>

            {isStudentsLoading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary spinner-border-sm mr-2"></div> Đang tải danh sách học viên...
                </div>
            ) : studentsData && studentsData.length > 0 ? (
                <div className="table-responsive rounded-lg border">
                    <Table striped hover className="mb-0" style={{ fontSize: '0.9rem', verticalAlign: 'middle' }}>
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: '60px' }} className="text-center">STT</th>
                                <th>Mã học viên</th>
                                <th>Họ và tên học viên</th>
                                <th>Số điện thoại</th>
                                <th>Ngày sinh</th>
                                <th>Email</th>
                                <th>Địa chỉ</th>
                                <th style={{ width: '120px' }} className="text-center">Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {studentsData.map((student, idx) => (
                                <tr key={student.id || student.Id}>
                                    <td className="text-center text-muted">{idx + 1}</td>
                                    <td>{student.pseudonym || student.identityCard || student.IdentityCard}</td>
                                    <td className="font-weight-bold text-dark">{student.fullName || student.FullName}</td>
                                    <td>{student.phoneNumber || student.PhoneNumber}</td>
                                    <td className="text-muted">
                                        {student.dateOfBirth || student.DateOfBirth ? moment(student.dateOfBirth || student.DateOfBirth).format('DD/MM/YYYY') : ''}
                                    </td>
                                    <td>{student.email || student.Email}</td>
                                    <td>{student.address || student.Address}</td>
                                    <td className="text-center">
                                        <Button variant="outline-primary" size="sm" className="mr-1" style={{ borderRadius: '6px' }} onClick={() => toast.info('Tính năng sửa đổi thông tin học viên đang hoàn thiện.')}>
                                            <i className="fas fa-edit"></i>
                                        </Button>
                                        <Button variant="outline-danger" size="sm" style={{ borderRadius: '6px' }} onClick={() => toast.info('Tính năng xóa học viên đang hoàn thiện.')}>
                                            <i className="fas fa-trash-alt"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <div className="text-center py-5 text-muted border rounded-lg bg-light">
                    <i className="fas fa-user-graduate fa-3x mb-3 text-muted" style={{ opacity: 0.5 }}></i>
                    <h5>Chưa có danh sách học viên</h5>
                    <p className="small mb-0">Tải lên file Excel để nhập danh sách học viên tham gia khóa học.</p>
                </div>
            )}

            {/* ADD STUDENT MODAL */}
            <Modal show={showAddStudentModal} onHide={() => setShowAddStudentModal(false)} centered>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="font-weight-bold">Thêm học viên mới</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small font-weight-bold">Họ và tên học viên <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ví dụ: Nguyễn Văn A..."
                                value={newStudent.fullName}
                                onChange={(e) => setNewStudent({ ...newStudent, fullName: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small font-weight-bold">Mã số học viên / CCCD <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="CCCD làm tên đăng nhập..."
                                value={newStudent.identityCard}
                                onChange={(e) => setNewStudent({ ...newStudent, identityCard: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small">Mã định danh lớp (Pseudonym)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Mã số học viên..."
                                value={newStudent.pseudonym}
                                onChange={(e) => setNewStudent({ ...newStudent, pseudonym: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small">Số điện thoại</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Số điện thoại..."
                                value={newStudent.phoneNumber}
                                onChange={(e) => setNewStudent({ ...newStudent, phoneNumber: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small">Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Email..."
                                value={newStudent.email}
                                onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0">
                    <Button variant="light" onClick={() => setShowAddStudentModal(false)} style={{ borderRadius: '8px' }}>
                        Hủy bỏ
                    </Button>
                    <Button variant="primary" onClick={handleSaveStudent} style={{ borderRadius: '8px' }}>
                        Lưu thông tin
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* CONFIRM SSO ACCOUNT CREATION MODAL */}
            <Modal show={showConfirmCreateAccountsModal} onHide={() => setShowConfirmCreateAccountsModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="font-weight-bold text-dark">
                        <i className="fas fa-user-plus mr-2 text-primary"></i>Phát hiện học viên chưa có tài khoản SSO
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <div className="alert alert-warning small shadow-sm mb-4">
                        <i className="fas fa-info-circle mr-2"></i>
                        Hệ thống đối chiếu danh sách CCCD và phát hiện <strong>{studentsToCreateAccounts.length} học viên</strong> dưới đây chưa có tài khoản trên hệ thống SSO. Nhấp <strong>Xác nhận</strong> để tự động tạo tài khoản hàng loạt (mật khẩu mặc định là <code>123456</code>).
                    </div>
                    <div className="table-responsive border rounded-lg bg-white" style={{ maxHeight: '280px' }}>
                        <Table hover size="sm" className="mb-0" style={{ fontSize: '0.85rem' }}>
                            <thead className="bg-light sticky-top">
                                <tr>
                                    <th className="text-center" style={{ width: '50px' }}>STT</th>
                                    <th>Số CCCD / Tài khoản</th>
                                    <th>Họ và tên học viên</th>
                                    <th>Mã lớp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentsToCreateAccounts.map((student, idx) => (
                                    <tr key={idx}>
                                        <td className="text-center text-muted">{idx + 1}</td>
                                        <td className="font-weight-bold text-dark">{student.identityCard}</td>
                                        <td>{student.fullName}</td>
                                        <td>{student.pseudonym || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0">
                    <Button variant="light" onClick={() => setShowConfirmCreateAccountsModal(false)} disabled={isCreatingAccounts} style={{ borderRadius: '8px' }}>
                        Bỏ qua
                    </Button>
                    <Button variant="primary" onClick={handleCreateAccounts} disabled={isCreatingAccounts} style={{ borderRadius: '8px' }}>
                        {isCreatingAccounts ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Đang tạo tài khoản...
                            </>
                        ) : 'Xác nhận tạo tài khoản'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StudentTab;
